import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ClientMeterType, ClientObjectType } from 'api/client'
import { RecipientTypeEnum } from 'Modules/Communication/shared/models/Inbox'
import {
  IRecipientModel,
  RecipientOpenState,
  IRecipientQueryModel,
  FilterTypeEnum,
  RecipientsSortByEnum,
} from 'Modules/Communication/shared/models/Recipients'
import { DEFAULT_PAGE_TAKE } from 'shared/constants/filters'
import { ISortOrderEnum } from 'shared/models/List'
import { RecipientState } from './selectors'

const initialState: RecipientState = {
  filters: {
    skip: 0,
    take: DEFAULT_PAGE_TAKE,
    searchText: '',
    sortcolumn: RecipientsSortByEnum.Name,
    sortorder: ISortOrderEnum.asc,
    filterType: FilterTypeEnum.Owners,
    buildingTypeId: null,
    buildingId: null,
    addressId: null,
    boardId: null,
    meterTypeNo: null,
    objectTypeNo: null,
  },

  openState: 'Email',
  openStateType: RecipientTypeEnum.To,
  listedRecipients: [],
  listedRecipientsRemovalQueue: [],
  objects: [],
  meters: [],
}

const messageRecipientsSlice = createSlice({
  name: 'messageRecipients',
  initialState,
  reducers: {
    /**
     * Filter
     */
    setFilters: (
      state,
      action: PayloadAction<
        IRecipientQueryModel | { [x: string]: number | string | null }
      >
    ) => {
      const isSwappingFilterType = Object.keys(action.payload).includes(
        'filterType'
      )

      // Sets default values for pre-fetched lists connected to filterType
      if (isSwappingFilterType) {
        const filterType = action.payload.filterType
        if (
          filterType === FilterTypeEnum.Meters &&
          state.meters.length &&
          !state.filters.meterTypeNo
        ) {
          state.filters.meterTypeNo = state.meters[0].meterTypeId!
        }

        if (
          filterType === FilterTypeEnum.Objects &&
          state.objects.length &&
          !state.filters.objectTypeNo
        ) {
          state.filters.objectTypeNo = state.objects[0].objectTypeId!
        }

        if (
          filterType === FilterTypeEnum.BoardMembers &&
          !state.filters.boardId
        ) {
          state.filters.boardId = 'all'
        }
      }

      const isNotHabitants =
        isSwappingFilterType &&
        (action.payload.filterType !== (FilterTypeEnum.Owners as string) ||
          action.payload.filterType !== (FilterTypeEnum.Tenants as string) ||
          action.payload.filterType !== (FilterTypeEnum.Cohabitant as string))

      if (isNotHabitants) {
        state.filters.addressId = null
        state.filters.buildingTypeId = null
      }

      // Checks whenever we swap filters that should reset the skip value
      const filtersToResetOn: string[] = [
        'boardId',
        'addressId',
        'buildingTypeId',
        'buildingId',
        'filterType',
        'meterTypeNo',
        'objectTypeNo',
        'searchText',
      ]
      const isSwappingFilterThatShouldReset = Object.keys(action.payload).map(
        (load) => filtersToResetOn.some((l) => l === load)
      )
      if (isSwappingFilterThatShouldReset) state.filters.skip = 0

      // only update filters sent in
      state.filters = {
        ...state.filters,
        ...action.payload,
      }

      if (
        isSwappingFilterType &&
        state.filters.filterType !== FilterTypeEnum.BoardMembers
      ) {
        state.filters.boardId = null
      }
    },

    /**
     * Reset
     */
    resetListedRecipients: (state) => {
      state.listedRecipients = []
      state.listedRecipientsRemovalQueue = []
      state.openStateType = null
    },
    reset: (state) => {
      state = initialState
    },
    resetObjectAndMetersFilter: (state) => {
      state.filters.meterTypeNo = null
      state.filters.objectTypeNo = null
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    onRecipientsClose: (state) => {
      state.listedRecipients = []
      state.listedRecipientsRemovalQueue = []
    },

    /**
     * Handle recipient
     */
    addRecipient: (state, action: PayloadAction<IRecipientModel>) => {
      state.listedRecipients.push(action.payload)
    },
    removeRecipient: (state, action: PayloadAction<string>) => {
      state.listedRecipients = state.listedRecipients.filter(
        (recipient) => recipient.nameId !== action.payload
      )
    },

    addRecipientToRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel>
    ) => {
      state.listedRecipientsRemovalQueue.push(action.payload)
    },
    removeRecipientFromRemovalQueue: (state, action: PayloadAction<string>) => {
      state.listedRecipientsRemovalQueue =
        state.listedRecipientsRemovalQueue.filter(
          (recipient) => recipient.nameId !== action.payload
        )
    },

    addRecipientsToRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel[]>
    ) => {
      // add action.payload to removal queue without duplicates
      state.listedRecipientsRemovalQueue = [
        ...state.listedRecipientsRemovalQueue,
        ...action.payload.filter(
          (recipient) =>
            !state.listedRecipientsRemovalQueue.some(
              (r) =>
                r.nameId === recipient.nameId ||
                r.email === recipient.email ||
                r.phonenumber === recipient.phonenumber
            )
        ),
      ]
    },

    removeRecipientsFromRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel[]>
    ) => {
      state.listedRecipientsRemovalQueue =
        state.listedRecipientsRemovalQueue.filter(
          (recipient) =>
            !action.payload.some(
              (r) =>
                r.nameId === recipient.nameId ||
                r.email === recipient.email ||
                r.phonenumber === recipient.phonenumber
            )
        )
    },

    removeAllRecipients: (state, action: PayloadAction<IRecipientModel[]>) => {
      state.listedRecipients = state.listedRecipients.filter(
        (recipient) =>
          !action.payload.find(
            (listedRecipient) => listedRecipient.nameId === recipient.nameId
          )
      )
    },
    addAllRecipients: (state, action: PayloadAction<IRecipientModel[]>) => {
      action.payload.forEach((recipient) => {
        if (
          state.listedRecipients.some(
            ({ nameId }) => recipient.nameId && nameId === recipient.nameId
          )
        )
          return

        state.listedRecipients.push(recipient)
      })
    },
    setOpenState: (
      state,
      action: PayloadAction<{
        open: RecipientOpenState
        type: RecipientTypeEnum | null
      }>
    ) => {
      state.openState = action.payload.open
      state.openStateType = action.payload.type
    },

    /**
     * Objects and meters
     */
    fetchObjectAndMetersTypesSuccess: (
      state,
      action: PayloadAction<{
        objects: ClientObjectType[]
        meters: ClientMeterType[]
      }>
    ) => {
      // Set filters to first object type number, avoid refresh of filters
      if (action.payload.objects.length) {
        state.filters.objectTypeNo = action.payload.objects[0].objectTypeId!
      } else {
        state.filters.objectTypeNo = undefined
      }

      if (action.payload.meters.length) {
        state.filters.meterTypeNo = action.payload.meters[0].meterTypeId!
      } else {
        state.filters.meterTypeNo = undefined
      }

      state.objects = action.payload.objects
      state.meters = action.payload.meters
    },
    fetchObjectAndMetersTypesError: (state) => {
      state.filters.objectTypeNo = undefined
    },
  },
})

export default messageRecipientsSlice
